import { useEffect, useRef } from 'react'

// O leitor "digita" com poucos ms entre as teclas; gente digitando fica bem acima disso. É a rapidez que
// separa os dois, não o tamanho: o cartão de comanda "15" também é leitura, e o Enter dele tem de parar aqui.
const INTERVALO_MAXIMO_MS = 60

interface OpcoesLeitor {
  ativo: boolean
  aoLer: (codigo: string) => void
  /** Leitura que não é produto (o cartão da própria comanda): o Enter some, mas nada acontece. */
  ignorar?: (codigo: string) => boolean
}

function emCampoEditavel(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false
  return alvo.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName)
}

/**
 * Leitor de código de barras (teclado USB) numa tela sem campo focado: junta a rajada de teclas e entrega
 * o código no Enter. Escuta na captura do `window` para segurar o Enter antes de ele acionar o botão focado.
 */
export function useLeitorCodigoBarras({ ativo, aoLer, ignorar }: OpcoesLeitor): void {
  const callbacks = useRef({ aoLer, ignorar })

  useEffect(() => {
    callbacks.current = { aoLer, ignorar }
  })

  useEffect(() => {
    if (!ativo) return

    let leitura = ''
    let ultimaTecla = 0

    const aoTeclar = (evento: KeyboardEvent): void => {
      if (emCampoEditavel(evento.target) || document.querySelector('[role="dialog"]')) {
        leitura = ''
        return
      }

      const agora = Date.now()
      if (agora - ultimaTecla > INTERVALO_MAXIMO_MS) leitura = ''
      ultimaTecla = agora

      if (evento.key === 'Enter') {
        const codigo = leitura
        leitura = ''
        if (!codigo) return

        evento.preventDefault()
        evento.stopPropagation()
        if (!callbacks.current.ignorar?.(codigo)) callbacks.current.aoLer(codigo)
        return
      }

      if (evento.key.length === 1) leitura += evento.key
    }

    window.addEventListener('keydown', aoTeclar, true)
    return () => window.removeEventListener('keydown', aoTeclar, true)
  }, [ativo])
}
